<template>
  <div class="pay-page">
    <!-- Header -->
    <div class="pay-header">
      <p class="pay-label">{{ t('payment.step') }}</p>
      <h2 class="pay-title">{{ t('payment.title') }}</h2>
      <p class="pay-subtitle">{{ t('payment.subtitle') }}</p>
    </div>

    <!-- Vehicle Selection -->
    <div v-if="store.sessionVehicles.length > 0" class="pay-card">
      <p class="pay-card-label">{{ t('vehicle.selectVehicles') }}</p>
      <p class="pay-card-hint">{{ t('vehicle.selectVehiclesHint') }}</p>
      <div class="vehicle-list">
        <label
          v-for="v in store.sessionVehicles"
          :key="v.id"
          class="vehicle-row"
          :class="{ 'vehicle-row-active': selectedVehicleIds.has(v.id) }"
        >
          <input
            type="checkbox"
            :checked="selectedVehicleIds.has(v.id)"
            @change="toggleVehicle(v.id)"
            class="vehicle-checkbox"
          />
          <span class="vehicle-name">{{ v.display_name || v.model }}</span>
          <span class="vehicle-vin">{{ v.vin }}</span>
        </label>
      </div>
      <p v-if="store.sessionVehicles.length > 1" class="pay-count">
        {{ selectedVehicleIds.size }} / {{ store.sessionVehicles.length }} {{ t('vehicle.selected') }}
      </p>
      <p v-if="selectedVehicleIds.size === 0" class="pay-warn">
        {{ t('vehicle.selectAtLeastOne') }}
      </p>
    </div>

    <!-- Loading quote -->
    <div v-if="quoteLoading" class="pay-loading">{{ t('payment.loadingQuote') }}</div>

    <!-- Quote -->
    <div v-if="quote" class="pay-card">
      <h3 class="pay-card-title">{{ t('payment.quoteSummary') }}</h3>
      <div class="quote-rows">
        <div class="quote-row">
          <span>{{ t('payment.basic') }} &middot; {{ t('payment.vehicleCount', { count: quote.vehicle_count }, quote.vehicle_count) }}</span>
          <span class="quote-amount">{{ formatCents(quote.tier_price_cents) }}</span>
        </div>
        <div class="quote-row quote-row-muted">
          <span>{{ t('payment.vatIncluded') }} ({{ quote.vat_rate }}%)</span>
          <span>{{ formatCents(quote.vat_cents) }}</span>
        </div>
        <div class="quote-divider"></div>
        <div class="quote-row quote-row-total">
          <span>{{ t('payment.total') }}</span>
          <span>{{ formatCents(quote.total_cents) }}</span>
        </div>
      </div>
    </div>

    <!-- Payment Method -->
    <div v-if="quote" class="pay-card">
      <h3 class="pay-card-title">{{ t('payment.selectMethod') }}</h3>
      <div class="method-grid">
        <button
          @click="selectedPaymentMethod = 'stripe'"
          class="method-btn"
          :class="{ 'method-btn-active': selectedPaymentMethod === 'stripe' }"
        >
          <img :src="visaLogo" alt="Visa" class="method-img" />
          <img :src="mastercardLogo" alt="Mastercard" class="method-img" />
        </button>
        <button
          @click="selectedPaymentMethod = 'googlepay'"
          class="method-btn"
          :class="{ 'method-btn-active': selectedPaymentMethod === 'googlepay' }"
        >
          <img :src="googlePayLogo" alt="Google Pay" class="method-img-lg" />
        </button>
        <button
          @click="selectedPaymentMethod = 'applepay'"
          class="method-btn"
          :class="{ 'method-btn-active': selectedPaymentMethod === 'applepay' }"
        >
          <img :src="applePayLogo" alt="Apple Pay" class="method-img-apple" />
        </button>
      </div>

      <!-- Terms -->
      <label class="terms-row">
        <input type="checkbox" v-model="agreedToTerms" class="terms-checkbox" />
        <span class="terms-text">
          {{ t('payment.agreePrefix') }}
          <button type="button" @click.prevent="showServiceAgreement = true" class="terms-link">
            {{ t('payment.serviceAgreementLink') }}
          </button>
          {{ t('payment.agreeSuffix') }}
        </span>
      </label>

      <!-- Pay Button -->
      <button
        @click="handlePay"
        :disabled="!selectedPaymentMethod || !agreedToTerms || selectedVehicleIds.size === 0 || store.isProcessing"
        class="split-btn"
        style="margin-top: 1rem;"
      >
        <span v-if="store.isProcessing">{{ t('payment.processing') }}</span>
        <span v-else-if="!agreedToTerms">{{ t('payment.mustAgreeFirst') }}</span>
        <span v-else-if="quote && quote.total_cents === 0">{{ t('payment.continueForFree') }}</span>
        <span v-else-if="selectedPaymentMethod && quote">{{ t('payment.pay') }} {{ formatCents(quote.total_cents) }}</span>
        <span v-else>{{ t('actions.payNow') }}</span>
      </button>

      <p class="pay-security">{{ t('payment.securityNote') }}</p>
    </div>

    <!-- Error -->
    <div v-if="store.error" class="split-error">{{ store.error }}</div>

    <!-- Service Agreement Modal -->
    <ServiceAgreement v-if="showServiceAgreement" @close="showServiceAgreement = false" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app';
import { trackEvent } from '@/utils/analytics';
import ServiceAgreement from '@/components/ServiceAgreement.vue';
import visaLogo from '@/assets/images/visa.png';
import mastercardLogo from '@/assets/images/mastercard.png';
import applePayLogo from '@/assets/images/apple-pay.png';
import googlePayLogo from '@/assets/images/google-pay.png';
import type { QuoteResponse } from '@/types';

const { t } = useI18n();
const store = useAppStore();

const selectedPaymentMethod = ref<'stripe' | 'googlepay' | 'applepay' | null>(store.paymentMethod || null);
const agreedToTerms = ref(false);
const showServiceAgreement = ref(false);

const DEFAULT_PLAN = 'default';
const selectedPlan = ref<string>(store.selectedPlan || DEFAULT_PLAN);

const quote = computed<QuoteResponse | null>(() => store.currentQuote);
const quoteLoading = computed(() => store.quoteLoading);

const selectedVehicleIds = ref<Set<number>>(
  store.sessionVehicles.length === 1
    ? new Set([store.sessionVehicles[0].id])
    : new Set()
);

const toggleVehicle = (id: number) => {
  store.setError(null);
  const next = new Set(selectedVehicleIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedVehicleIds.value = next;
};

const formatCents = (cents: number): string => `€${(cents / 100).toFixed(2)}`;

async function loadQuote() {
  if (selectedVehicleIds.value.size === 0) return;
  await store.loadQuote(selectedPlan.value, Array.from(selectedVehicleIds.value));
}

onMounted(async () => {
  const data = await store.loadCatalog();
  if (data && data.packages.length > 0) {
    const pkg = data.packages[0];
    selectedPlan.value = pkg.name;
    trackEvent('select_item', {
      item_id: pkg.name,
      item_name: pkg.label,
      price: (pkg.tiers[0]?.price_cents ?? 0) / 100,
      currency: data.currency,
    });
  }
  if (selectedVehicleIds.value.size > 0) await loadQuote();
});

let quoteDebounceTimer: ReturnType<typeof setTimeout> | null = null;
watch(selectedVehicleIds, () => {
  if (selectedVehicleIds.value.size > 0) {
    if (quoteDebounceTimer) clearTimeout(quoteDebounceTimer);
    quoteDebounceTimer = setTimeout(() => loadQuote(), 400);
  } else {
    store.setCurrentQuote(null);
  }
}, { deep: true });

const handlePay = async () => {
  if (!selectedPaymentMethod.value) return;
  const sessionId = store.sessionId;
  if (!sessionId) {
    store.setError('Session expired. Please start over.');
    store.setStep('landing');
    return;
  }
  store.setProcessing(true);
  store.setError(null);
  try {
    const vehicleIds = Array.from(selectedVehicleIds.value);
    if (vehicleIds.length === 0) {
      store.setError(t('vehicle.selectAtLeastOne'));
      store.setProcessing(false);
      return;
    }
    const paymentMethod = selectedPaymentMethod.value === 'googlepay' || selectedPaymentMethod.value === 'applepay'
      ? 'stripe'
      : selectedPaymentMethod.value;
    trackEvent('begin_checkout', {
      payment_provider: paymentMethod,
      value: quote.value ? quote.value.total_cents / 100 : undefined,
      currency: quote.value?.currency,
    });
    const result = await store.createPayment(paymentMethod as 'stripe');
    store.setSelectedPlan(selectedPlan.value);
    store.setPaymentMethod(selectedPaymentMethod.value);
    if (quote.value && quote.value.total_cents === 0) {
      store.setProcessing(false);
      store.setStep('generating');
      return;
    }
    if (result.redirectUrl) {
      window.location.href = result.redirectUrl;
      return;
    }
    store.setStripeClientSecret(result.clientSecret ?? null);
    store.setProcessing(false);
    store.setStep('payment-gateway');
  } catch (error) {
    console.error('Payment error:', error);
    store.setError(error instanceof Error ? error.message : 'Failed to initiate payment');
    store.setProcessing(false);
  }
};
</script>

<style scoped>
.pay-page {
  max-width: 640px;
  margin: 0 auto;
  padding: 3rem 1.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.pay-header { text-align: center; margin-bottom: 0.5rem; }
.pay-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.7);
  margin-bottom: 0.5rem;
}
.pay-title {
  color: var(--white);
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 0.5rem;
}
.pay-subtitle { color: rgba(255,255,255,0.75); font-size: 0.95rem; line-height: 1.6; }

.pay-card {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.pay-card-label { color: var(--white); font-weight: 700; font-size: 0.9rem; }
.pay-card-hint { color: rgba(255,255,255,0.65); font-size: 0.82rem; margin-top: -0.25rem; }
.pay-card-title { color: var(--white); font-weight: 700; font-size: 1rem; margin-bottom: 0.25rem; }

.vehicle-list { display: flex; flex-direction: column; gap: 0.5rem; }
.vehicle-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.1);
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
}
.vehicle-row:hover { background: rgba(255,255,255,0.05); }
.vehicle-row-active { background: rgba(255,255,255,0.07); border-color: rgba(255,255,255,0.25); }
.vehicle-checkbox { accent-color: var(--lime); width: 1rem; height: 1rem; flex-shrink: 0; }
.vehicle-name { color: var(--white); font-weight: 600; font-size: 0.9rem; flex: 1; }
.vehicle-vin { color: rgba(255,255,255,0.55); font-size: 0.75rem; font-family: monospace; }

.pay-count { color: rgba(255,255,255,0.65); font-size: 0.82rem; }
.pay-warn { color: rgba(239,68,68,0.9); font-size: 0.82rem; }
.pay-loading { color: rgba(255,255,255,0.65); font-size: 0.875rem; text-align: center; padding: 0.5rem; }

.quote-rows { display: flex; flex-direction: column; gap: 0.5rem; }
.quote-row { display: flex; justify-content: space-between; align-items: center; color: var(--white); font-size: 0.9rem; padding: 0.2rem 0; }
.quote-row-muted { color: rgba(255,255,255,0.8); font-size: 0.82rem; }
.quote-row-total { font-weight: 800; font-size: 1rem; padding-top: 0.5rem; }
.quote-amount { font-weight: 700; }
.quote-divider { border-top: 1px solid rgba(255,255,255,0.1); margin: 0.25rem 0; }

.method-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.75rem; }
.method-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.875rem 0.5rem;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.05);
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
}
.method-btn:hover { background: rgba(255,255,255,0.09); }
.method-btn-active { border-color: var(--lime); background: rgba(200,241,53,0.1); }
.method-img { height: 1.25rem; object-fit: contain; }
.method-img-lg { height: 1.9rem; object-fit: contain; }
.method-img-apple { height: 2.6rem; object-fit: contain; filter: brightness(0) invert(1); }

.terms-row {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-top: 0.25rem;
  cursor: pointer;
}
.terms-checkbox { accent-color: var(--lime); width: 1rem; height: 1rem; flex-shrink: 0; margin-top: 0.15rem; }
.terms-text { color: rgba(255,255,255,0.75); font-size: 0.8rem; line-height: 1.5; }
.terms-link { color: var(--white); text-decoration: underline; background: none; border: none; cursor: pointer; font-size: 0.8rem; padding: 0; }

.pay-security { color: rgba(255,255,255,0.7); font-size: 0.75rem; text-align: center; margin-top: 0.5rem; }

@media (max-width: 480px) {
  .method-grid { grid-template-columns: 1fr; }
  .pay-page { padding: 2rem 1rem 3rem; }
}
</style>
